// src/types/coverage-types.ts

import { ArtifactType } from './artifact-digest-types';

// Enums

/**
 * Primary categories of personal injury insurance coverage.
 */
export enum CoverageCategory {
  /** Client's own auto coverage: MedPay, PIP (Personal Injury Protection), Collision, Comprehensive, UM/UIM. This policy belongs to or directly benefits the client named in the case. */
  FIRST_PARTY = 'FIRST_PARTY',
  /** Client's primary health insurance (e.g., Blue Cross, Aetna, Medicare, Medicaid). This policy covers the client's medical expenses. */
  FIRST_PARTY_HEALTH = 'FIRST_PARTY_HEALTH',
  /** At-fault party's or another involved party's auto coverage: BI (Bodily Injury), PD (Property Damage), etc. This policy does NOT belong to the client. */
  THIRD_PARTY = 'THIRD_PARTY',
  /** Coverage for vehicles/people that were involved but are currently believed not at fault. */
  NOT_LIABLE_INVOLVED_PARTY = 'NOT_LIABLE_INVOLVED_PARTY',
  /** Coverage category is unclear or cannot be determined. */
  UNCLEAR = 'UNCLEAR',
  /** Premises property/general liability insurance for the incident location. */
  PROPERTY = 'PROPERTY',
  /** Workers' compensation insurance covering workplace injuries. */
  WORKERS_COMPENSATION = 'WORKERS_COMPENSATION',
}

/**
 * Types of insurance benefits we are interested in extracting.
 */
export enum BenefitType {
  PERSONAL_INJURY_PROTECTION = 'PERSONAL_INJURY_PROTECTION',
  MEDICAL_PAYMENTS = 'MEDICAL_PAYMENTS',
  UNINSURED_MOTORIST = 'UNINSURED_MOTORIST',
  UNDER_INSURED_MOTORIST = 'UNDER_INSURED_MOTORIST',
  BODILY_INJURY_LIABILITY = 'BODILY_INJURY_LIABILITY',
  PROPERTY_DAMAGE_LIABILITY = 'PROPERTY_DAMAGE_LIABILITY',
  COMPREHENSIVE_AUTO = 'COMPREHENSIVE_AUTO',
  COLLISION_AUTO = 'COLLISION_AUTO',
  WORKERS_COMPENSATION = 'WORKERS_COMPENSATION',
  OTHER = 'OTHER',
}

/**
 * Types of policy identifiers.
 */
export enum IdentifierType {
  POLICY_NUMBER = 'POLICY_NUMBER',
  MEMBER_ID = 'MEMBER_ID',
  GROUP_NUMBER = 'GROUP_NUMBER',
  OTHER = 'OTHER',
}

/**
 * Types of insurance policies.
 */
export enum PolicyType {
  AUTO = 'AUTO',
  HOMEOWNERS = 'HOMEOWNERS',
  COMMERCIAL = 'COMMERCIAL',
  HEALTH = 'HEALTH',
  UMBRELLA = 'UMBRELLA',
  GENERAL_LIABILITY = 'GENERAL_LIABILITY',
  OTHER = 'OTHER',
}

/**
 * Sub-categories for other types of insurance coverage.
 */
export enum CoverageSubCategory {
  /** Homeowners liability coverage. */
  HOMEOWNERS_LIABILITY = 'HOMEOWNERS_LIABILITY',
  /** Homeowners property coverage. */
  HOMEOWNERS_PROPERTY = 'HOMEOWNERS_PROPERTY',
  /** Commercial General Liability (CGL) coverage. */
  COMMERCIAL_GENERAL_LIABILITY = 'COMMERCIAL_GENERAL_LIABILITY',
  /** Professional Liability, Errors & Omissions (E&O), or Malpractice insurance. */
  PROFESSIONAL_LIABILITY = 'PROFESSIONAL_LIABILITY',
  /** Personal umbrella liability coverage. */
  UMBRELLA_PERSONAL = 'UMBRELLA_PERSONAL',
  /** Commercial umbrella liability coverage. */
  UMBRELLA_COMMERCIAL = 'UMBRELLA_COMMERCIAL',
  /** Workers' compensation insurance. */
  WORKERS_COMPENSATION = 'WORKERS_COMPENSATION',
  /** Other liability coverage, e.g., Boating, Renter's liability. */
  OTHER_LIABILITY = 'OTHER_LIABILITY',
  /** Fallback for unclassified or less common types of coverage. */
  OTHER_COVERAGE = 'OTHER_COVERAGE',
}

/**
 * Recommended actions when comparing a new coverage mention with existing records.
 */
export enum RecommendedCoverageAction {
  /** Add the newly identified coverage as a new, distinct record. */
  ADD_AS_NEW = 'ADD_AS_NEW',
  /** Merge the newly identified coverage information with an existing coverage record. */
  MERGE_WITH_EXISTING = 'MERGE_WITH_EXISTING',
  /** Add the coverage but mark it as unclear. */
  ADD_AS_UNCLEAR = 'ADD_AS_UNCLEAR',
}

/**
 * Represents the processing status of a coverage mention.
 */
export enum MentionStatus {
  /** Initial state, pending processing. */
  PENDING = 'PENDING',
  /** Actively being processed. */
  PROCESSING = 'PROCESSING',
  /** Processing complete, ready for review or next steps. */
  READY = 'READY',
  /** Coverage mention has been reviewed and accepted. */
  ACCEPTED = 'ACCEPTED',
  /** Coverage mention has been reviewed and rejected. */
  REJECTED = 'REJECTED',
  /** An error occurred during processing. */
  ERROR = 'ERROR',
}

export enum CoverageStatus {
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  DUPLICATE = 'DUPLICATE',
}

/** Tracks the client's explicit decision on a coverage, independent of status. */
export enum ClientDecision {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

export enum CoverageLiabilityStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  PARTIALLY_ACCEPTED = 'PARTIALLY_ACCEPTED',
  LIABILITY_UNKNOWN = 'LIABILITY_UNKNOWN',
  UNDER_INVESTIGATION = 'UNDER_INVESTIGATION',
}

/**
 * Source of enrichment data for carrier information.
 */
export enum EnrichmentSource {
  PERPLEXITY = 'perplexity',
  CACHED = 'cached',
  MANUAL = 'manual',
  INTAKE = 'intake',
  EXTRACTION = 'extraction',
}

// Base types

/**
 * Metadata about the enrichment source when coverage data is cached.
 */
export interface EnrichmentMetadata {
  /** Type of the enrichment source */
  source_type: 'cached_coverage' | 'perplexity_search' | 'manual_entry';
  /** ID of the source matter when data is cached */
  source_matter_id?: string;
  /** Name of the source matter when data is cached */
  source_matter_name?: string;
  /** ID of the source coverage record when data is cached */
  source_coverage_id?: string;
  /** When the source coverage was approved */
  approved_at?: string;
  /** Age of the cached data in days */
  cache_age_days?: number;
  /** Reason why this cache match was selected */
  match_reason?: string;
  /** Normalized carrier name used for matching */
  carrier_normalized_name?: string;
}

/**
 * Represents a contact method.
 */
export interface ContactHandleSchema {
  /** The type of contact method (e.g., "PHONE", "EMAIL", "WEBSITE"). */
  handle_type: string;
  /** The value of the contact method (e.g., an email address or phone number). */
  value: string;
  /** Optional name/description of the contact method. */
  name?: string;
  /** Optional sources that provided this contact information. */
  sources?: WebSource[];
}

export interface MergeCoverageRequest {
  /** The ID of the destination coverage record that will receive the merged data. */
  dst_coverage_id: string;
  /** List of source coverage record IDs whose data will be merged into the destination. */
  src_coverage_ids: string[];
}

/**
 * Represents a web source with URL and optional metadata.
 */
export interface WebSource {
  /** The URL of the web source. */
  url: string;
  /** Optional title of the web page or source. */
  title?: string;
  /** Optional description of the web page or source content. */
  description?: string;
}

/**
 * A generic key-value pair for storing coverage-related details.
 */
export interface CoverageKeyedValue {
  /** The key or label for the detail. */
  key: string;
  /** The value associated with the key. */
  value: string;
}

export interface LorAutofillReviewPayload {
  template_id?: string | null;
  template_name?: string | null;
  merge_fields: Record<string, unknown>;
  document_id?: string | null;
  thread_id?: string | null;
}

/**
 * Response from starting an async LOR review workflow.
 */
export interface StartLorReviewResponse {
  workflow_id: string;
  coverage_id: string;
}

/**
 * Status values for the LOR review workflow.
 */
export type LorReviewStatus = 'pending' | 'reviewing' | 'completed' | 'failed';

/**
 * Response from querying LOR review workflow status.
 */
export interface LorReviewWorkflowStatusResponse {
  workflow_id: string;
  status: LorReviewStatus;
  status_description: string;
  is_running: boolean;
  started_at?: string | null;
  completed_at?: string | null;
  error_message?: string | null;
  thread_id?: string | null;
}

/**
 * Details about a specific benefit limit.
 */
export interface BenefitLimit {
  /**
   * Description of any qualifier to the numeric limit, e.g., 'per person', 'per accident'.
   */
  key: string;
  /**
   * Numeric limit as expressed in the text.
   */
  value: number;
}

/**
 * Details about a specific policy attribute.
 */
export interface PolicyDetail extends CoverageKeyedValue {
  /**
   * A well-formatted label for the policy detail, i.e "VIN", "Vehicle Description", "Premium", "Insured Address", "Garage Address".
   */
  key: string;
  /**
   * The value of the policy detail, i.e "1234567890", "Toyota Camry", "100000.00".
   */
  value: string;
}

/**
 * Details about a subrogator.
 */
export interface SubrogatorDetail {
  /**
   * Name of the subrogator handling a related claim if explicitly stated, otherwise null.
   */
  name: string | null;
  /**
   * The BEST (at MOST 2 handles per type) contact methods for the subrogator (phone, email, fax) if explicitly stated,
   * otherwise null.
   */
  handles: ContactHandleSchema[] | null;
  /**
   * List of sources that provided the subrogator information if explicitly stated, otherwise null.
   */
  sources: WebSource[] | null;
}

/**
 * Details about an insurance carrier.
 */
export interface CarrierDetail {
  /**
   * The colloquial, common form, well-known name of the insurance carrier if mentioned, null otherwise.
   * This is a key field for matching and deduplication.
   */
  name: string | null;
  /**
   * The BEST (at _MOST_ 2 handles per type) web pages or phone numbers for submitting and managing claims if mentioned, null otherwise.
   */
  handles: ContactHandleSchema[] | null;
  /**
   * The headquarters address of the insurance carrier if mentioned, null otherwise.
   */
  address: string | null;
}

// =============================================================================
// Property Contact Types (for Premises Liability cases)
// =============================================================================

/**
 * Type of property contact for premises liability cases.
 */
export enum PropertyContactType {
  PROPERTY_OWNER = 'PROPERTY_OWNER',
  PROPERTY_MANAGER = 'PROPERTY_MANAGER',
  OTHER = 'OTHER',
}

/**
 * Category of the property contact recipient for task branching.
 * Used after Send LOR to determine follow-up task type:
 * - CORPORATE_ENTITY / REGISTERED_AGENT / PROPERTY_MANAGEMENT → Open Premises Claim
 * - PRIVATE_RESIDENT → Research & Identify Alternate Contacts
 */
export enum PropertyRecipientCategory {
  CORPORATE_ENTITY = 'CORPORATE_ENTITY',
  REGISTERED_AGENT = 'REGISTERED_AGENT',
  PROPERTY_MANAGEMENT = 'PROPERTY_MANAGEMENT',
  PRIVATE_RESIDENT = 'PRIVATE_RESIDENT',
  GOVERNMENT = 'GOVERNMENT',
}

export interface MailingAddress {
  address_line1: string;
  address_line2?: string | null;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
}

/**
 * Contact information for property-related parties in premises liability cases.
 * Used to track property owners, managers, or other relevant contacts for a premises property.
 */
export interface PropertyContact {
  /** Stable identifier for the property contact record. */
  id: string;
  /** The type of contact (owner, manager, or other). */
  contact_type: PropertyContactType;
  /** Custom label when contact_type is OTHER (e.g., 'Tenant', 'Security Company'). */
  custom_type_label?: string | null;
  /** Name of the contact person or entity. */
  name?: string | null;
  /** Contact methods: phone, email, fax, etc. */
  handles?: ContactHandleSchema[] | null;
  /** Legacy freeform mailing address string for display/backwards compatibility. */
  address?: string | null;
  /** Structured mailing address used for certified mail/correspondence. */
  mailing_address?: MailingAddress | null;
  /** Category of the recipient (drives follow-up task branching after Send LOR). */
  recipient_category?: PropertyRecipientCategory | null;
  /** Additional notes about this contact. */
  notes?: string | null;
}

/**
 * Details about a benefit covered by the policy.
 */
export interface BenefitDetail {
  /**
   * A limited categorization of insurance benefits we are interested in extracting.
   */
  benefit_type: BenefitType;
  /**
   * Structured list of applicable coverage limitations if explicitly stated, otherwise null.
   * e.g., {"per person": 100000, "per accident": 1000000}
   */
  limitations: Record<string, number> | null;
  /**
   * Whether stacking benefits apply for UM/UIM coverage. When true, coverage limits from
   * multiple policies can be combined. Only applicable for UNINSURED_MOTORIST and
   * UNDER_INSURED_MOTORIST benefit types. Null indicates unknown/not specified.
   */
  is_stacked?: boolean | null;
  /**
   * Metadata about the source of this benefit data (BPO, user edit, system, etc.).
   * Null indicates unknown/legacy data for backwards compatibility.
   */
  source?: CoverageDataSourceInfo | null;
}

/**
 * Policy identifier with type and value.
 */
export interface PolicyIdentifier {
  /**
   * The type of identifier for the policy.
   */
  identifier_type: IdentifierType;
  /**
   * The value of the identifier for the policy.
   */
  identifier_value: string;
  /**
   * Metadata about the source of this policy identifier data.
   * None indicates unknown/legacy data for backwards compatibility.
   */
  source?: CoverageDataSourceInfo | null;
}

/**
 * Details about a vehicle covered by the policy.
 */
export interface VehicleDetail {
  /**
   * The make of the vehicle if explicitly stated, otherwise null.
   */
  make: string | null;
  /**
   * The model of the vehicle if explicitly stated, otherwise null.
   */
  model: string | null;
  /**
   * The year of the vehicle if explicitly stated, otherwise null.
   */
  year: number | null;
  /**
   * The VIN of the vehicle if explicitly stated, otherwise null.
   */
  vin: string | null;
  /**
   * The license plate of the vehicle if explicitly stated, otherwise null.
   */
  license_plate: string | null;
  /**
   * The color of the vehicle if explicitly stated, otherwise null.
   */
  color: string | null;
  /**
   * The license plate state code if explicitly stated, otherwise null.
   */
  license_plate_state: string | null;
}

/**
 * Source of coverage data (claim numbers, adjuster info, etc.).
 */
export enum CoverageDataSource {
  /** Backwards compatibility / unspecified */
  UNKNOWN = 'UNKNOWN',
  /** Added by BPO agent */
  BPO = 'BPO',
  /** Edited by a user */
  USER_EDIT = 'USER_EDIT',
  /** Added by system (e.g., coverage mention workflow) */
  SYSTEM = 'SYSTEM',
  /** Extracted by voice AI from call audio/transcript */
  VOICE_AI = 'VOICE_AI',
}

/**
 * Metadata about the source of coverage data.
 */
export interface CoverageDataSourceInfo {
  /** The source that created or last modified this data. */
  source: CoverageDataSource;
  /** User ID if source is USER_EDIT. */
  user_id?: string | null;
  /** User display name if source is USER_EDIT (for display purposes). */
  user_name?: string | null;
  /** When the data was added/modified from this source. */
  timestamp?: string | null;
}

/**
 * Details about a claim against the policy.
 */
export interface ClaimDetail {
  /**
   * The benefit against which the claim is being made, if explicitly stated, otherwise null.
   */
  benefit_type: BenefitType | null;
  /**
   * The claim identifier (number, id, etc.) for the policy, if explicitly stated, otherwise null.
   */
  claim_identifier: string | null;
  /**
   * Metadata about the source of this claim data (BPO, user edit, system, etc.).
   * Null indicates unknown/legacy data for backwards compatibility.
   */
  source?: CoverageDataSourceInfo | null;
}

/**
 * Details about an adjuster.
 */
export interface AdjusterDetail {
  /**
   * The benefit the adjuster is specifically assigned to, if explicitly stated, otherwise null.
   */
  benefit_type: BenefitType | null;
  /**
   * Name of the adjuster handling a related claim if explicitly stated, otherwise null.
   */
  name: string | null;
  /**
   * The BEST (at MOST 2 handles per type) contact methods for the adjuster (phone, email, fax) if explicitly stated,
   * otherwise null.
   */
  handles: ContactHandleSchema[] | null;
  /**
   * Metadata about the source of this adjuster data (BPO, user edit, system, etc.).
   * Null indicates unknown/legacy data for backwards compatibility.
   */
  source?: CoverageDataSourceInfo | null;
}

// Main schemas

/**
 * Base schema for representing insurance coverage information.
 */
export interface BaseCoverageSchema {
  /**
   * The type of policy if explicitly stated, otherwise null.
   */
  policy_type: PolicyType | null;
  /**
   * Details about the insurance carrier if mentioned, otherwise null.
   */
  carrier: CarrierDetail | null;
  /**
   * Carrier-issued identifiers for the policy, if explicitly stated, otherwise null.
   */
  policy_identifiers: PolicyIdentifier[] | null;
  /**
   * List of names of primary policyholders or insured entities if stated or implied, otherwise null.
   */
  named_insured: string[] | null;
  /**
   * List of benefits covered by the policy, if explicitly stated, otherwise null.
   */
  benefits: BenefitDetail[] | null;
  /**
   * Any known adjusters handling claims against this policy, if explicitly stated, otherwise null.
   */
  adjusters: AdjusterDetail[] | null;
  /**
   * Any known claims opened against the policy, if explicitly stated, otherwise null.
   */
  claims: ClaimDetail[] | null;
  /**
   * List of vehicles covered by the policy, if applicable and explicitly stated, otherwise null.
   */
  vehicles: VehicleDetail[] | null;
  /**
   * Structured list of extracted policy details, such as, but not limited to:
   * VIN; vehicle description; premium amount; insured's address;
   * garage location; policy endorsements; coverage exclusions;
   * other policy-specific information for claims processing.
   */
  policy_details: PolicyDetail[] | null;
  /**
   * Details about the subrogator if health insurance policy + carrier ... if explicitly stated, otherwise null.
   */
  subrogator: SubrogatorDetail | null;
  /**
   * For PROPERTY category coverages (premises liability): list of property owners, managers,
   * or other relevant contacts. Each contact can receive an LOR. Not applicable for standard
   * insurance coverages.
   */
  property_contacts?: PropertyContact[] | null;
  /**
   * The category of the policy if explicitly stated, otherwise null.
   */
  category: CoverageCategory | null;
  /**
   * Source of enrichment data for carrier information.
   */
  enrichment_source?: EnrichmentSource | null;
  /**
   * Metadata about enrichment source (source matter ID, match reason, etc.).
   */
  enrichment_metadata?: EnrichmentMetadata | null;
  /**
   * Whether cached data was used for enrichment.
   */
  cache_used?: boolean;
}

/**
 * Schema for creating a new coverage record.
 */
export interface CreateCoverageSchema extends BaseCoverageSchema {
  /** The ID of the matter this coverage belongs to. */
  matter_id: string;
  /** Selected carrier (directory id) */
  carrier_v2_id?: string | null;
  /** Selected division id */
  carrier_division_id?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: string | null;
}

/**
 * Schema for updating an existing coverage record.
 */
export interface UpdateCoverageSchema extends BaseCoverageSchema {
  /** The ID of the coverage to update. */
  id: string;
  /** The status of the coverage. */
  status: CoverageStatus | null;
  /** The liability determination status for this coverage. */
  liability_status?: CoverageLiabilityStatus | null;
  /** Reason for denial when liability is rejected or partially accepted. */
  denial_reason?: string | null;
  /** New: selected carrier (directory id) */
  carrier_v2_id?: string | null;
  /** New: selected division id */
  carrier_division_id?: string | null;
  /** Selected LoR document to associate with review + send workflows. */
  lor_document_id?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: string | null;
  /** Whether this is the primary coverage within its party (1P or 3P). */
  is_primary?: boolean;
}

/**
 * Schema for representing a mention of coverage extracted from text or conversation.
 */
export interface CoverageExtractionResultSchema extends BaseCoverageSchema {
  /**
   * Probability (0-1) that this mention of coverage is relevant to the case, and that the
   * classification is accurate.
   */
  confidence: number;
  /**
   * 1-3 concise and readable sentences defending the confidence score: cite the cues that likely
   * tie the coverage to the case - what indications led to its classification.
   */
  reasoning: string;
  /**
   * Up to three verbatim snippets from the text that support the extracted coverage mention.
   * Include enough leading context to be self-explanatory.
   */
  excerpts: string[];

  /**
   * Short, comprehensive sentence succinctly describing where this mention of coverage was found.
   */
  context?: string | null;
}

/**
 * Schema for the result of validating a coverage mention.
 */
export interface CoverageValidationResultSchema {
  /**
   * Determine if this mention actually mentions an insurance policy (TRUE) rather than just a generic
   * reference to 'insurance' (FALSE). Only mark as valid if there's a concrete policy instance that could
   * be verified.
   */
  is_valid_coverage_mention: boolean;
  /**
   * Evaluate how uniquely and specifically identifiable this coverage is from the mention alone, using a
   * 0.0-1.0 scale. Refer to internal documentation for the detailed scoring rubric.
   * (Full rubric:
   * 0.0 - 0.2: Vague mention (e.g., 'insurance', 'a policy') with no carrier or type.
   * 0.3 - 0.4: Mention of insurance, without implied insured party, or without a carrier name.
   * 0.5 - 0.6: Clear implication of an insured party AND a carrier name.
   * 0.7 - 0.8: Mentions carrier, policy number, and type, but some ambiguity remains or details are partial.
   * 0.9 - 1.0: Clear mention of carrier, policy number, specific coverage type, and insured/claimant context.
   * )
   */
  coverage_specificity_score: number;
  /**
   * Justify your specificity score with concrete examples from the text. Explain which criteria from the
   * scoring rubric apply and why this mention rates at its assigned level of specificity.
   */
  specificity_score_reasoning: string;
}

/**
 * Schema for search queries used in coverage information enrichment.
 */
export interface CoverageEnrichmentQuerySchema {
  /**
   * List of queries to search for coverage information.
   */
  queries: string[];
}

/**
 * Represents the final candidate Coverage entry after evaluating source material, extraction, and web search results.
 */
export interface CandidateCoverageResult {
  /** Candidate Coverage to integrate into the Matter. */
  candidate_coverage: BaseCoverageSchema;
  /**
   * Provide a concise, professional summary of what information was found during enrichment,
   * which sources were consulted, and any key limitations or uncertainties encountered.
   * Limit to 2-3 sentences.
   */
  enrichment_summary: string;
  /**
   * Assess the overall reliability of the enriched data on a 0.0-1.0 scale, considering the
   * quality of sources, consistency of information, and completeness of key fields.
   */
  enrichment_confidence: number;
}

/**
 * Schema for recommending an action when comparing a new coverage mention with existing ones.
 */
export interface CoverageComparisonRecommendationSchema {
  /**
   * Select the single most appropriate action based on your analysis. For clear unique
   * instances, choose ADD_AS_NEW. For the same underlying policy with some variations,
   * choose MERGE_WITH_EXISTING.
   */
  action_recommended: RecommendedCoverageAction;
  /**
   * If recommending MERGE_WITH_EXISTING, specify the ID of the existing coverage record that
   * best matches. Required for this action, must be null otherwise.
   */
  target_existing_coverage_id: string | null;
  /**
   * Assess your confidence (0.0-1.0) in the recommended action. Consider quality of
   * matching evidence, clarity of differences, and consistency of key fields. Higher
   * scores (>0.8) should only be used when evidence is very strong.
   */
  confidence_score: number;
  /**
   * Provide thorough, specific reasoning for your recommendation, referencing exact field
   * matches and differences. Explain the weight given to key PI categories (1P/PIP, 3P/BI,
   * Health) in your decision. Include analysis of both supporting and contradicting
   * evidence.
   */
  reasoning: string;
  /**
   * Document specific fields that match between the new and existing coverage records and
   * how they match. Use descriptions like 'exact_match', 'normalized_match' (e.g.,
   * 'Geico'/'GEICO'), 'partial_match', or 'conflicting_values' for each field.
   */
  identified_overlapping_fields: Record<string, string> | null;
  /**
   * Identify specific new or updated information in the current mention that would enhance
   * the existing record if merged. Structure as field_name: new_value pairs. Include only
   * information that's truly additive or more precise than existing data.
   */
  new_information_identified: Record<string, string> | null;
}

/**
 * Base schema for a coverage mention, encompassing its lifecycle and associated data.
 */
export interface BaseCoverageMentionSchema {
  /** The processing status of the coverage mention. */
  status: MentionStatus | null;
  /** Results from the coverage extraction process, if available. */
  extraction: CoverageExtractionResultSchema | null;
  /** Results from the coverage validation process, if available. */
  validation: CoverageValidationResultSchema | null;
  /** Queries used for enriching coverage information, if applicable. */
  search_queries: CoverageEnrichmentQuerySchema | null;
  /** Results obtained from the enrichment search, if performed. */
  search_results: unknown[] | null; // Consider defining a more specific type if possible
  /** The candidate coverage information after processing and enrichment. */
  candidate: CandidateCoverageResult | null;
  /** List of existing coverage entities that may relate to this mention. */
  existing_entities: CoverageSchema[] | null;
  /** Recommendation for integrating this mention with existing coverages. */
  integration: CoverageComparisonRecommendationSchema | null;
  /** A patch object to update an existing coverage based on this mention. */
  patch: UpdateCoverageSchema | null;
  /** User feedback or corrections related to this mention. */
  feedback: Record<string, string> | null;
}

/**
 * Schema for creating a new coverage mention.
 */
export interface CreateCoverageMentionSchema extends BaseCoverageMentionSchema {
  /** The ID of the artifact from which this coverage mention was extracted. */
  artifact_id: string;
}

/**
 * Schema for updating an existing coverage mention.
 */
export interface UpdateCoverageMentionSchema extends BaseCoverageMentionSchema {
  /** The ID of the coverage mention to update. */
  id: string;
  /** The ID of the canonical coverage record this mention is linked to, if any. */
  coverage_id: string | null;
}

/**
 * Represents a specific mention of coverage identified within an artifact.
 */
export interface CoverageMentionSchema extends BaseCoverageMentionSchema {
  /** Unique identifier for the coverage mention. */
  id: string;
  /** Identifier of the artifact associated with this mention. */
  artifact_id: string;
  /** The type of the artifact associated with this mention. */
  artifact_type: ArtifactType;
  /** The ID of the canonical coverage record this mention is linked to, if any. */
  coverage_id: string | null;
  /** Timestamp when the mention was created. */
  created_at: string;
}

export interface CoverageMentionStub {
  /** Unique identifier for the coverage mention. */
  id: string;
  /** Timestamp when the mention was created. */
  created_at: string;
  /** Identifier of the artifact associated with this mention. */
  artifact_id: string;
  /** The type of the artifact associated with this mention. */
  artifact_type: ArtifactType;
  /** The IDs of the citations that support this mention...if they exist */
  citation_ids: string[];
  /** The confidence score of the coverage mention. */
  confidence: number | null;
  /** One liner on where the mention was found. */
  context: string | null;
}

export interface LorUploadedArtifactSummary {
  id: string;
  blob_id?: string | null;
  name?: string | null;
  original_filename?: string | null;
  file_extension?: string | null;
  file_mime_type?: string | null;
  created_at?: string | null;
}

/**
 * Schema for representing a canonical coverage record.
 */
export interface CoverageSchema extends BaseCoverageSchema {
  /** Unique identifier for the coverage record. */
  id: string;
  /** Identifier of the matter this coverage belongs to. */
  matter_id: string;
  /**
   * List of mentions of this coverage in the matter, if any.
   */
  status: CoverageStatus | null;
  liability_status: CoverageLiabilityStatus | null;
  /** Reason for denial when liability is rejected or partially accepted. */
  denial_reason?: string | null;
  mentions: CoverageMentionStub[] | null;
  /** New: selected carrier and division references */
  carrier_v2_id?: string | null;
  carrier_division_id?: string | null;
  /** Selected LoR document tied to this coverage, if any. */
  lor_document_id?: string | null;
  /** Uploaded LoR artifact tied to this coverage, if any. */
  lor_uploaded_artifact_id?: string | null;
  lor_uploaded_artifact?: LorUploadedArtifactSummary | null;
  /** AI-discovered context for opening a claim with citations and confidence scores */
  open_claim_context?: OpenClaimContextSchema | null;
  /** User ID who created this coverage (null for system/AI). */
  created_by_user_id?: string | null;
  /** Display name of user who created this coverage. */
  created_by_user_name?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: string | null;
  /** Whether this is the primary coverage within its party (1P or 3P). */
  is_primary?: boolean;
  /** Client's explicit decision on this coverage (null = no decision yet). */
  client_decision?: ClientDecision | null;
}

// =============================================================================
// Coverage List Endpoint — Discriminated union types
// =============================================================================

export enum CoverageSource {
  OWNED = 'owned',
  SHARED = 'shared',
}

export interface SharedToMatter {
  matter_id: string;
  matter_name: string;
}

/** Coverage owned by the queried matter, with outbound sharing info. */
export interface OwnedCoverageListItem extends CoverageSchema {
  source: CoverageSource.OWNED;
  shared_to_count: number;
  shared_to_matters: SharedToMatter[];
}

/** Coverage shared to the queried matter from another (inbound). */
export interface SharedCoverageListItem extends CoverageSchema {
  source: CoverageSource.SHARED;
  primary_matter_id: string;
  primary_matter_name: string;
}

export type CoverageListItem = OwnedCoverageListItem | SharedCoverageListItem;

export interface CoverageListResponse {
  results: CoverageListItem[];
}

/**
 * Stub interface for coverage used in task serialization.
 */
export interface CoverageStub {
  /** Unique identifier for the coverage record. */
  id: string;
  /** The primary, classified category of this insurance coverage. */
  category: CoverageCategory | null;
  /** Details about the insurance carrier. */
  carrier: CarrierDetail | null;
  /** Carrier-issued identifiers for the policy, if explicitly stated, otherwise null. */
  policy_identifiers: PolicyIdentifier[] | null;
  /** List of names of primary policyholders or insured entities. */
  named_insured: string[] | null;
  /** The type of policy if explicitly stated, otherwise null. */
  policy_type: PolicyType | null;
}

// =============================================================================
// BPO Context Types
// =============================================================================

/**
 * Type of source document for a data field.
 */
export enum SourceType {
  POLICE_REPORT = 'police_report',
  INTAKE = 'intake',
  COVERAGE_DOC = 'coverage_doc',
  PARTY_RECORD = 'party_record',
  INCIDENT_NOTE = 'incident_note',
  UNKNOWN = 'unknown',
}

/**
 * Reference to a source document for a data field.
 */
export interface SourceReference {
  /** Artifact ID for InspectArtifactButton navigation */
  artifact_id: string | null;
  /** Fallback blob ID for document navigation */
  blob_id: string | null;
  /** Type of source document */
  source_type: SourceType;
  /** Human-readable label, e.g. 'Police Report #123' */
  source_label: string;
}

/**
 * A data field with its source citation.
 */
export interface CitedField<T> {
  value: T;
  source: SourceReference | null;
}

/**
 * Vehicle type enum.
 */
export enum VehicleType {
  PASSENGER_CAR = 'passenger_car',
  TRUCK = 'truck',
  MOTORCYCLE = 'motorcycle',
  BUS = 'bus',
  BICYCLE = 'bicycle',
  SCOOTER = 'scooter',
  SKATEBOARD = 'skateboard',
  ROLLER_SKATE = 'roller_skate',
  ROLLER_BLADE = 'roller_blade',
  TRAILER = 'trailer',
  OTHER = 'other',
}

/**
 * Vehicle from the Vehicle table (subjects).
 */
export interface ReadVehicle {
  id: string;
  matter_id: string;
  make: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  vin: string | null;
  license_plate: string | null;
  license_plate_state_code: string | null;
  vehicle_type: VehicleType;
  estimated_speed_mph: number | null;
  label_in_police_report: string | null;
  short_description: string | null;
  involvement_description: string | null;
  created_at: string;
  modified_at: string;
}

/**
 * A vehicle with its current coverage assignment and source.
 */
export interface VehicleAssignment {
  vehicle: ReadVehicle;
  /** Coverage ID this vehicle is assigned to, null = unassigned */
  assigned_coverage_id: string | null;
  /** Where this vehicle was extracted from */
  source: SourceReference | null;
}

/**
 * Summary of a coverage's vehicle assignment status.
 */
export interface CoverageVehicleSummary {
  coverage_id: string;
  category: CoverageCategory;
  carrier_name: string | null;
  carrier_phone: string | null;
  policy_number: string | null;
  /** Policyholder name from coverage */
  named_insured: string | null;
  /** Policyholder address from coverage policy_details */
  named_insured_address: string | null;
  /** Claim number from coverage claims */
  claim_number: string | null;
  /** Current Coverage.vehicles assignments */
  assigned_vehicles: VehicleDetail[];
  /** Coverage document source */
  source: SourceReference | null;
}

/**
 * At-fault driver information resolved from multiple sources.
 */
export interface AtFaultDriverInfo {
  name: string | null;
  address: string | null;
  phone: string | null;
  /** Source document for this at-fault driver info */
  source: SourceReference | null;
}

/**
 * Incident schema (simplified for BPO context).
 */
export interface IncidentSchema {
  id: string;
  matter_id: string;
  description: string | null;
  date_of_loss: string | null;
  time_of_loss: string | null;
  location: string | null;
  weather_conditions: string | null;
  injuries_described: string | null;
  incident_type: string | null;
  hospital: string | null;
  ambulance: string | null;
  police_report: string | null;
  current_treatment: string | null;
  witnesses: string | null;
  statements: string | null;
  photos: string | null;
  representation: string | null;
  created_at: string;
  modified_at: string;
}

/**
 * All data needed for the BPO review modal with source citations.
 */
export interface BPOContextSchema {
  matter_id: string;

  /** All vehicles from the Vehicle table with their sources */
  all_vehicles: VehicleAssignment[];

  /** All coverages for the matter with their vehicle assignments */
  coverages: CoverageVehicleSummary[];

  /** Firm info (with sources) */
  firm_name: CitedField<string | null> | null;
  firm_address: CitedField<string | null> | null;

  /** Client info (with sources) */
  client_name: CitedField<string | null> | null;
  client_phone: CitedField<string | null> | null;
  client_email: CitedField<string | null> | null;
  client_address: CitedField<string | null> | null;
  client_city: CitedField<string | null> | null;
  client_state: CitedField<string | null> | null;
  client_zip: CitedField<string | null> | null;
  client_dob: CitedField<string | null> | null;
  client_last_four_ssn: CitedField<string | null> | null;

  /** Matter-level location (from Matter model) */
  matter_city: CitedField<string | null> | null;
  matter_state: CitedField<string | null> | null;

  /** Matter info (with sources) */
  date_of_loss: CitedField<string | null> | null;
  time_of_loss: CitedField<string | null> | null;
  location: CitedField<string | null> | null;
  weather_conditions: CitedField<string | null> | null;

  /** Incident info */
  incident: IncidentSchema | null;
  /** Explicit incident description field for BPO */
  incident_description: CitedField<string | null> | null;

  /** Inferred from incident description */
  vehicle_drivable: string | null;
  vehicle_towed: string | null;

  /** At-fault driver (with source) - resolved across multiple sources */
  at_fault_driver: AtFaultDriverInfo | null;
}

/**
 * Specifies a vehicle field to clear from open_claim_context AI suggestions.
 */
export interface ClearVehicleAiFieldRequest {
  /** Vehicle ID whose AI suggestion should be cleared */
  vehicle_id: string;
  /** Field name to clear (e.g., 'year', 'make', 'model', 'color', 'vin', 'license_plate') */
  field: string;
}

/**
 * Request schema for assigning vehicles to a coverage.
 */
export interface AssignVehiclesRequest {
  /** Vehicle IDs from Vehicle table to assign to this coverage */
  vehicle_ids: string[];
  /** Vehicle AI suggestion fields to clear from open_claim_context after assignment */
  clear_ai_fields?: ClearVehicleAiFieldRequest[];
}

/**
 * Request schema for updating BPO context fields.
 */
export interface UpdateBPOContextRequest {
  /** Update the incident description */
  incident_description?: string | null;
  /** Update date of loss (YYYY-MM-DD format) */
  date_of_loss?: string | null;
  /** Update time of loss (e.g., 2:30 PM or 14:30) */
  time_of_loss?: string | null;
  /** Update incident location */
  location?: string | null;
  /** Update weather conditions */
  weather_conditions?: string | null;
  /** Update client name */
  client_name?: string | null;
  /** Update client date of birth (YYYY-MM-DD format) */
  client_dob?: string | null;
  /** Update client phone */
  client_phone?: string | null;
  /** Update client email */
  client_email?: string | null;
  /** Update client address */
  client_address?: string | null;
  /** Update client city */
  client_city?: string | null;
  /** Update client state */
  client_state?: string | null;
  /** Update client ZIP code */
  client_zip?: string | null;
  /** Update client last four of SSN */
  client_last_four_ssn?: string | null;
  /** Update matter city (incident location) */
  matter_city?: string | null;
  /** Update matter state (incident location) */
  matter_state?: string | null;
  /** Update at-fault driver name */
  at_fault_driver_name?: string | null;
  /** Update at-fault driver phone */
  at_fault_driver_phone?: string | null;
  /** Update at-fault driver address */
  at_fault_driver_address?: string | null;
  /** Coverage ID (required for coverage field updates) */
  coverage_id?: string | null;
  /** Update carrier name */
  carrier_name?: string | null;
  /** Update policy number */
  policy_number?: string | null;
  /** Update carrier phone */
  carrier_phone?: string | null;
  /** Update named insured (policyholder) */
  named_insured?: string | null;
  /** Update named insured address (policyholder address) */
  named_insured_address?: string | null;
  /** Update claim number */
  claim_number?: string | null;
}

// =============================================================================
// Open Claim Context Types
// =============================================================================

/**
 * Status of a field in the open claim context.
 */
export enum OpenClaimFieldStatus {
  /** Value confirmed from authoritative source */
  VERIFIED = 'VERIFIED',
  /** Value found but needs human verification */
  SUGGESTED = 'SUGGESTED',
  /** Required value not found in documents */
  MISSING = 'MISSING',
  /** Optional value not searched or not applicable */
  UNKNOWN = 'UNKNOWN',
  /** Pre-populated value was incorrect (e.g., claim number stored as policy number) */
  WRONG = 'WRONG',
}

/**
 * A data field with citation and confidence information.
 */
export interface OpenClaimCitedField<T> {
  /** The discovered or existing value for this field */
  value: T | null;
  /** The verification status of this field */
  status: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value */
  confidence: number | null;
  /** Citation IDs linking to source text in documents */
  citation_ids: string[];
  /** Artifact ID of the primary source document */
  source_artifact_id: string | null;
  /** Brief explanation of why this value was suggested or verified */
  reasoning: string | null;
}

/**
 * Client information needed for opening a claim.
 */
export interface OpenClaimClientInfo {
  name: OpenClaimCitedField<string>;
  phone: OpenClaimCitedField<string>;
  email: OpenClaimCitedField<string>;
  address: OpenClaimCitedField<string>;
  city: OpenClaimCitedField<string>;
  state: OpenClaimCitedField<string>;
  zip_code: OpenClaimCitedField<string>;
  dob: OpenClaimCitedField<string>;
  last_four_ssn: OpenClaimCitedField<string>;
}

/**
 * Incident details needed for opening a claim.
 */
export interface OpenClaimIncidentInfo {
  date_of_loss: OpenClaimCitedField<string>;
  time_of_loss: OpenClaimCitedField<string>;
  location: OpenClaimCitedField<string>;
  weather_conditions: OpenClaimCitedField<string>;
  incident_description: OpenClaimCitedField<string>;
}

/**
 * Vehicle information for claims.
 */
export interface OpenClaimVehicleInfo {
  year: OpenClaimCitedField<number | null>;
  make: OpenClaimCitedField<string>;
  model: OpenClaimCitedField<string>;
  vin: OpenClaimCitedField<string>;
  license_plate: OpenClaimCitedField<string>;
  license_plate_state: OpenClaimCitedField<string>;
  color: OpenClaimCitedField<string>;
  /** UUID of the coverage this vehicle should be assigned to */
  suggested_coverage_id: OpenClaimCitedField<string>;
}

/**
 * Insurance coverage details needed for opening a claim.
 */
export interface OpenClaimCoverageInfo {
  carrier_name: OpenClaimCitedField<string>;
  policy_number: OpenClaimCitedField<string>;
  carrier_phone: OpenClaimCitedField<string>;
  named_insured: OpenClaimCitedField<string>;
  claim_number: OpenClaimCitedField<string>;
}

/**
 * At-fault/other party driver information.
 */
export interface OpenClaimAtFaultDriverInfo {
  name: OpenClaimCitedField<string>;
  phone: OpenClaimCitedField<string>;
  address: OpenClaimCitedField<string>;
}

/**
 * Complete context for opening an insurance claim.
 */
export interface OpenClaimContextSchema {
  /** Client/claimant information */
  client: OpenClaimClientInfo;
  /** Details about the incident/accident */
  incident: OpenClaimIncidentInfo;
  /** Client's vehicle(s) involved in the incident */
  client_vehicles: OpenClaimVehicleInfo[];
  /** Insurance coverage details */
  coverage: OpenClaimCoverageInfo;
  /** At-fault or other party driver information */
  at_fault_driver: OpenClaimAtFaultDriverInfo;
  /** At-fault or other party vehicle information */
  defendant_vehicle: OpenClaimVehicleInfo;
  /** City where the incident/matter is located */
  matter_city: OpenClaimCitedField<string>;
  /** State where the incident/matter is located */
  matter_state: OpenClaimCitedField<string>;
  /** ISO timestamp when AI discovery completed */
  discovery_completed_at: string | null;
  /** Summary of what was found and what's missing */
  discovery_summary: string | null;
}

/**
 * Request schema for updating open claim context fields.
 */
export interface UpdateOpenClaimContextRequest {
  client_name?: string | null;
  client_phone?: string | null;
  client_email?: string | null;
  client_address?: string | null;
  client_city?: string | null;
  client_state?: string | null;
  client_zip?: string | null;
  client_dob?: string | null;
  client_last_four_ssn?: string | null;
  date_of_loss?: string | null;
  time_of_loss?: string | null;
  location?: string | null;
  weather_conditions?: string | null;
  incident_description?: string | null;
  carrier_name?: string | null;
  policy_number?: string | null;
  carrier_phone?: string | null;
  named_insured?: string | null;
  claim_number?: string | null;
  at_fault_driver_name?: string | null;
  at_fault_driver_phone?: string | null;
  at_fault_driver_address?: string | null;
  matter_city?: string | null;
  matter_state?: string | null;
  defendant_vehicle_year?: number | null;
  defendant_vehicle_make?: string | null;
  defendant_vehicle_model?: string | null;
  defendant_vehicle_vin?: string | null;
  defendant_vehicle_license_plate?: string | null;
  defendant_vehicle_license_plate_state?: string | null;
  defendant_vehicle_color?: string | null;
}

// =============================================================================
// Companion Coverage Sharing Types
// =============================================================================

/**
 * Coverage summary for a companion member in the review modal.
 */
export interface CompanionCoverageSummary {
  id: string;
  category: CoverageCategory | null;
  carrier_name: string | null;
  policy_number: string | null;
  status: CoverageStatus | null;
  is_primary: boolean;
  shared_to_matter_ids?: string[];
}

/**
 * A companion member with their coverages for the review modal.
 */
export interface CompanionMemberCoverages {
  matter_id: string;
  matter_name: string;
  is_primary: boolean;
  coverages: CompanionCoverageSummary[];
}

/**
 * Response for the companion coverages review modal context.
 */
export interface CompanionCoveragesReviewResponse {
  members: CompanionMemberCoverages[];
  /** The resolved primary matter ID (companion_root_id). */
  primary_matter_id: string;
}

/**
 * A single association between a source coverage and target matter.
 */
export interface CoverageAssociation {
  source_coverage_id: string;
  target_matter_id: string;
}

/**
 * Request to complete the Find Coverage task with associations.
 */
export interface CompleteCompanionCoveragesRequest {
  associations: CoverageAssociation[];
}

/**
 * Response from completing companion coverages.
 */
export interface CompleteCompanionCoveragesResponse {
  status: 'ok';
  links_added: number;
  links_removed: number;
  coverages_marked_duplicate: number;
}
